/**
 * Locale Configuration Types
 *
 * Type definitions for project-level locale management.
 * Stores user-defined locales in Firestore.
 * Codes are free-form BCP 47 strings (not restricted to Language enum).
 *
 * @module locale-config/types
 */

// ============================================================================
// Core Types
// ============================================================================

/**
 * A single locale entry in the project.
 * Code is any valid BCP 47 string — not restricted to schema Language enum.
 */
export interface ProjectLocale {
  /** BCP 47 locale code: "en-US", "es-419", "nb", "zh-Hans" */
  code: string;
  /** Human-readable label: "English (US)", "Spanish (Latin America)" */
  label: string;
}

/**
 * Target platform for an onboarding.
 * Affects locale resolution strategy and preview rendering.
 */
export type OnboardingPlatform = 'ios' | 'android' | 'web';

// ============================================================================
// Project-local localization authoring
// ============================================================================

export type ProjectLocaleResourceStatus = 'active' | 'archived';

/**
 * Reusable market/brand context for localized project content. The profile is
 * intentionally product-neutral: funnels, onboarding flows, ads, and future
 * authored surfaces can resolve the same immutable project policy.
 */
export interface ProjectLocaleProfileContext {
  targetMarket?: string;
  tone?: string;
  marketDirection?: string;
  creativeContext?: {
    audience?: string;
    offer?: string;
    angle?: string;
    brand?: string;
    destinationSummary?: string;
  };
  claims?: string[];
  protectedTokens?: string[];
}

export interface ProjectLocaleProfile {
  id: string;
  projectId: string;
  version: number;
  status: ProjectLocaleResourceStatus;
  sourceLocale: string;
  targetLocales: string[];
  context: ProjectLocaleProfileContext;
  glossaryRef?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectLocaleGlossary {
  id: string;
  projectId: string;
  version: number;
  status: ProjectLocaleResourceStatus;
  sourceLocale: string;
  targetLocales: string[];
  entries: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectLocaleProfileInput {
  id: string;
  sourceLocale: string;
  targetLocales: string[];
  context: ProjectLocaleProfileContext;
  glossaryRef?: string;
}

export interface UpdateProjectLocaleProfileInput {
  sourceLocale?: string;
  targetLocales?: string[];
  context?: ProjectLocaleProfileContext;
  /** Null explicitly removes the glossary binding. */
  glossaryRef?: string | null;
}

export interface CreateProjectLocaleGlossaryInput {
  id: string;
  sourceLocale: string;
  targetLocales: string[];
  entries: Record<string, string>;
}

export interface UpdateProjectLocaleGlossaryInput {
  sourceLocale?: string;
  targetLocales?: string[];
  entries?: Record<string, string>;
}

export interface ProjectLocaleResourceListOptions {
  includeArchived?: boolean;
}

// ============================================================================
// Firestore Document
// ============================================================================

/**
 * Project-level locale configuration stored in Firestore.
 * Path: projects/{projectId}/settings/localeConfig
 *
 * All project-authoring locales are stored, including catalog and custom codes.
 * A missing document is previewed with DEFAULT_LOCALE_CONFIG until the user
 * explicitly saves the project language setup.
 */
export interface ProjectLocaleConfig {
  version: 1;

  /**
   * Ordered list of project locales.
   * First locale is typically the primary content locale.
   */
  locales: ProjectLocale[];

  /**
   * Default locale code (must exist in locales[]).
   * Used as fallback when device locale doesn't match any available key.
   */
  defaultLocale: string;

  updatedAt: string;
}

/**
 * Default locale config for new projects or when no config exists in Firestore.
 */
export const DEFAULT_LOCALE_CONFIG: ProjectLocaleConfig = {
  version: 1,
  locales: [{ code: 'en-US', label: 'English (US)' }],
  defaultLocale: 'en-US',
  updatedAt: '',
};
